import { Container, Sprite, type Texture } from 'pixi.js';
import { PixelBuf } from '../core/pixelfx';
import { fxArt } from './enemyart';

/**
 * 1.22 [매혹] 캐릭터 위 도트 이펙트 — 어느 캐릭터든 한눈에 "홀렸구나" 보이게.
 *
 *   - 머리 위를 도는 하트 셋 (어지러운 별 대신 하트, 뒤로 돌 때는 작고 흐리게)
 *   - 몸에서 톡톡 떠오르는 작은 하트
 *   - 끌려가는 요괴까지 이어진 하트 점선 — 요괴 쪽으로 흘러간다 (무엇에 끌려가는지)
 *   - 걸리는 순간: 받은 그림의 빛나는 하트가 머리 위에서 터지고 작은 하트가 흩어진다
 *   - 몸 색이 분홍으로 두근두근 (game.ts 가 입힌다)
 *
 * 하트는 코드로 한 칸씩 찍은 도트다 (게임 크기 1배, 최근접 확대).
 */

type RGB = [number, number, number];
const PAL: Record<string, RGB> = {
  X: [92, 12, 52], // 테두리
  D: [205, 36, 110], // 그늘
  P: [255, 84, 160], // 몸
  H: [255, 172, 212], // 밝은 곳
  W: [255, 255, 255], // 반짝
};

function pix(rows: string[]): Texture {
  const h = rows.length;
  const w = rows[0].length;
  const b = new PixelBuf(w, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const c = PAL[rows[y][x]];
    if (c) b.set(x, y, c);
  }
  return b.toTexture();
}

// 9×8 — 도는 하트
const BIG = [
  '.XX...XX.',
  'XWHX.XPPX',
  'XHPPXPPPX',
  'XPPPPPPDX',
  '.XPPPPDX.',
  '..XPPDX..',
  '...XDX...',
  '....X....',
];
// 7×6 — 떠오르는 하트
const MID = [
  '.XX.XX.',
  'XWPXPPX',
  'XPPPPDX',
  '.XPPDX.',
  '..XDX..',
  '...X...',
];
// 5×4 — 점선 하트 (테두리 없이 밝게)
const SMALL = [
  'HP.PP',
  'PPPPD',
  '.PPD.',
  '..D..',
];

interface Part {
  sp: Sprite;
  t: number;
  life: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  wob: number;
}

const ORBIT = 3;
const PARTS = 24;
const LINKS = 34;

export class CharmFx {
  private readonly root = new Container();
  private texBig: Texture | null = null;
  private texMid: Texture | null = null;
  private texSmall: Texture | null = null;
  private readonly orbit: Sprite[] = [];
  private readonly parts: Part[] = [];
  private readonly links: Sprite[] = [];
  private readonly burst = new Sprite();
  private burstT = -1;
  private spin = 0;
  private flow = 0;
  private puffT = 0;
  private fade = 0;

  constructor(layer: Container) {
    layer.addChild(this.root);
    this.root.zIndex = 1e6;
    this.burst.anchor.set(0.5);
    this.burst.blendMode = 'add';
    this.burst.zIndex = 10;
    this.burst.visible = false;
    this.root.addChild(this.burst);
  }

  private ensure(): void {
    if (this.texBig) return;
    this.texBig = pix(BIG);
    this.texMid = pix(MID);
    this.texSmall = pix(SMALL);
    for (let i = 0; i < LINKS; i++) {
      const s = new Sprite(this.texSmall);
      s.anchor.set(0.5);
      s.visible = false;
      this.root.addChild(s);
      this.links.push(s);
    }
    for (let i = 0; i < PARTS; i++) {
      const s = new Sprite(this.texMid);
      s.anchor.set(0.5);
      s.visible = false;
      s.zIndex = 2;
      this.root.addChild(s);
      this.parts.push({ sp: s, t: 0, life: 0, x: 0, y: 0, vx: 0, vy: 0, wob: 0 });
    }
    for (let i = 0; i < ORBIT; i++) {
      const s = new Sprite(this.texBig);
      s.anchor.set(0.5);
      s.visible = false;
      this.root.addChild(s);
      this.orbit.push(s);
    }
  }

  reset(): void {
    this.fade = 0;
    this.burstT = -1;
    this.burst.visible = false;
    for (const s of this.orbit) s.visible = false;
    for (const s of this.links) s.visible = false;
    for (const p of this.parts) {
      p.life = 0;
      p.sp.visible = false;
    }
  }

  /** 걸린 순간 — 머리 위에서 빛나는 하트가 터지고 작은 하트가 흩어진다 */
  start(x: number, headY: number): void {
    this.ensure();
    const art = fxArt('heart_burst');
    if (art) {
      this.burst.texture = art.frames[1] ?? art.frames[0];
      this.burstT = 0;
      this.burst.position.set(Math.round(x), Math.round(headY - 6));
      this.burst.visible = true;
    }
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + Math.random() * 0.4;
      this.puff(x, headY + 6, Math.cos(a) * 70, Math.sin(a) * 50 - 30, 0.55);
    }
  }

  private puff(x: number, y: number, vx: number, vy: number, life: number): void {
    const p = this.parts.find((q) => q.life <= 0);
    if (!p) return;
    p.x = x;
    p.y = y;
    p.vx = vx;
    p.vy = vy;
    p.t = 0;
    p.life = life;
    p.wob = Math.random() * 6;
    p.sp.visible = true;
  }

  /**
   * 매 스텝. on = 홀린 동안, k = 남은 비율(1 → 0), (x, feetY) 발, headY 머리 꼭대기, (sx, sy) 끌어당기는 요괴
   */
  update(dt: number, on: boolean, k: number, x: number, feetY: number, headY: number, sx: number, sy: number): void {
    if (!this.texBig) {
      if (!on) return;
      this.ensure();
    }
    this.fade = on ? Math.min(1, this.fade + dt / 0.15) : Math.max(0, this.fade - dt / 0.3);
    const f = this.fade;

    // 머리 위를 도는 하트 셋 — 풀릴 때가 다가오면 느려진다
    this.spin += dt * (2.2 + 2.4 * k);
    for (let i = 0; i < ORBIT; i++) {
      const s = this.orbit[i];
      s.visible = f > 0.01;
      if (!s.visible) continue;
      const a = this.spin + (i * Math.PI * 2) / ORBIT;
      const front = Math.sin(a);
      const bob = Math.sin(this.spin * 2.3 + i) * 1.5;
      s.position.set(Math.round(x + Math.cos(a) * 15), Math.round(headY - 7 + front * 4 + bob));
      const sc = front > 0 ? 1 : 0.75;
      s.scale.set(sc);
      s.alpha = f * (front > 0 ? 1 : 0.55);
      s.zIndex = front > 0 ? 3 : 1;
    }

    // 몸에서 톡톡 떠오르는 작은 하트
    if (on) {
      this.puffT -= dt;
      if (this.puffT <= 0) {
        this.puffT = 0.2;
        const bodyH = Math.max(20, feetY - headY);
        this.puff(x + (Math.random() - 0.5) * 22, feetY - bodyH * (0.35 + Math.random() * 0.45), (Math.random() - 0.5) * 14, -34 - Math.random() * 16, 0.9);
      }
    }
    for (const p of this.parts) {
      if (p.life <= 0) continue;
      p.t += dt;
      if (p.t >= p.life) {
        p.life = 0;
        p.sp.visible = false;
        continue;
      }
      p.vy *= 1 - dt * 1.5;
      p.vx *= 1 - dt * 2.5;
      p.x += p.vx * dt + Math.sin(p.t * 9 + p.wob) * 8 * dt;
      p.y += p.vy * dt;
      const u = p.t / p.life;
      p.sp.position.set(Math.round(p.x), Math.round(p.y));
      p.sp.alpha = u < 0.15 ? u / 0.15 : u > 0.6 ? (1 - u) / 0.4 : 1;
      p.sp.scale.set(u < 0.2 ? 0.6 + 2 * u : 1);
    }

    // 요괴까지 이어진 하트 점선 — 요괴 쪽으로 흘러간다
    const dx = sx - x;
    const dy = sy - (feetY - 20);
    const len = Math.hypot(dx, dy);
    this.flow = (this.flow + dt * 46) % 18;
    let used = 0;
    if (f > 0.01 && len > 30) {
      const ux = dx / len;
      const uy = dy / len;
      for (let d = 18 + this.flow; d < len - 16 && used < LINKS; d += 18) {
        const s = this.links[used++];
        const edge = Math.min(1, (d - 12) / 40, (len - d) / 40);
        const wave = Math.sin(d * 0.08 - this.spin * 3) * 3;
        s.position.set(Math.round(x + ux * d - uy * wave), Math.round(feetY - 20 + uy * d + ux * wave));
        s.alpha = f * 0.85 * Math.max(0, edge);
        s.visible = true;
      }
    }
    for (let i = used; i < LINKS; i++) this.links[i].visible = false;

    // 걸린 순간의 빛나는 하트
    if (this.burstT >= 0) {
      this.burstT += dt;
      const u = this.burstT / 0.55;
      if (u >= 1) {
        this.burstT = -1;
        this.burst.visible = false;
      } else {
        this.burst.position.set(Math.round(x), Math.round(headY - 8 - u * 10));
        this.burst.scale.set(0.35 + 0.55 * Math.sqrt(u));
        this.burst.alpha = u < 0.2 ? u / 0.2 : (1 - u) / 0.8;
      }
    }
    this.root.sortChildren();
  }
}
